import moment from 'moment';

export const TRIAL_BALANCE_UNITS = {
  unit: { label: 'Đồng', divisor: 1 },
  thousand: { label: 'Nghìn đồng', divisor: 1000 },
  million: { label: 'Triệu đồng', divisor: 1000000 },
};

export const TRIAL_BALANCE_AMOUNT_FIELDS = [
  'OPENINGDEBIT',
  'OPENINGCREDIT',
  'YTDDEBIT',
  'YTDCREDIT',
  'DEBITAMOUNT',
  'CREDITAMOUNT',
  'CLOSINGDEBIT',
  'CLOSINGCREDIT',
];

export const getTrialBalancePeriod = (quickPeriod, referenceDate = moment()) => {
  const date = moment(referenceDate);
  if (!date.isValid()) return { from: null, to: null };

  switch (quickPeriod) {
    case 'month':
      return { from: date.clone().startOf('month').format('DD/MM/YYYY'), to: date.clone().endOf('month').format('DD/MM/YYYY') };
    case 'previous_month': {
      const previousMonth = date.clone().subtract(1, 'month');
      return {
        from: previousMonth.startOf('month').format('DD/MM/YYYY'),
        to: previousMonth.endOf('month').format('DD/MM/YYYY'),
      };
    }
    case 'quarter':
      return { from: date.clone().startOf('quarter').format('DD/MM/YYYY'), to: date.clone().endOf('quarter').format('DD/MM/YYYY') };
    case 'previous_quarter': {
      const previousQuarter = date.clone().subtract(1, 'quarter');
      return {
        from: previousQuarter.startOf('quarter').format('DD/MM/YYYY'),
        to: previousQuarter.endOf('quarter').format('DD/MM/YYYY'),
      };
    }
    case 'year':
      return { from: date.clone().startOf('year').format('DD/MM/YYYY'), to: date.format('DD/MM/YYYY') };
    case 'previous_year': {
      const previousYear = date.clone().subtract(1, 'year');
      return {
        from: previousYear.startOf('year').format('DD/MM/YYYY'),
        to: previousYear.endOf('year').format('DD/MM/YYYY'),
      };
    }
    default:
      return { from: null, to: null };
  }
};

export const validateTrialBalancePeriod = (from, to) => {
  if (!from || !to) return 'Vui lòng chọn đầy đủ thời gian hạch toán.';
  const start = moment(from, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);
  const end = moment(to, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);
  if (!start.isValid() || !end.isValid()) return 'Thời gian hạch toán không hợp lệ.';
  if (start.isAfter(end)) return 'Ngày bắt đầu không được lớn hơn ngày kết thúc.';
  return null;
};

export const getTrialBalanceScale = (unit = 'unit') => TRIAL_BALANCE_UNITS[unit] || TRIAL_BALANCE_UNITS.unit;

export const scaleTrialBalanceAmount = (value, unit = 'unit') => {
  const number = Number(`${value ?? 0}`.replace(/,/g, ''));
  return Number.isFinite(number) ? number / getTrialBalanceScale(unit).divisor : 0;
};

export const getBalanceChecks = (summary = {}) => {
  const debitMovement = Number(summary.DEBITAMOUNT || 0);
  const creditMovement = Number(summary.CREDITAMOUNT || 0);
  const openingDebit = Number(summary.OPENINGDEBIT || 0);
  const openingCredit = Number(summary.OPENINGCREDIT || 0);
  const closingDebit = Number(summary.CLOSINGDEBIT || 0);
  const closingCredit = Number(summary.CLOSINGCREDIT || 0);

  return {
    movementDifference: debitMovement - creditMovement,
    closingEquationDifference: openingDebit + debitMovement - openingCredit - creditMovement - closingDebit + closingCredit,
  };
};

const compareAccountCodes = (left, right) =>
  `${left?.ACCOUNTINGACCOUNTCODE || ''}`.localeCompare(
    `${right?.ACCOUNTINGACCOUNTCODE || ''}`,
    'vi',
    { numeric: true, sensitivity: 'base' },
  );

export const buildTrialBalanceTree = (rows = []) => {
  const normalizedRows = rows.map((row, index) => ({
    ...row,
    __treeKey: String(row.ACCOUNTINGACCOUNTID ?? row.ACCOUNTINGACCOUNTCODE ?? index),
    __sourceIndex: index,
  }));
  const rowsById = new Map(
    normalizedRows
      .filter((row) => row.ACCOUNTINGACCOUNTID !== null && row.ACCOUNTINGACCOUNTID !== undefined)
      .map((row) => [String(row.ACCOUNTINGACCOUNTID), row]),
  );
  const rowsByCode = new Map(
    normalizedRows
      .filter((row) => row.ACCOUNTINGACCOUNTCODE)
      .map((row) => [String(row.ACCOUNTINGACCOUNTCODE), row]),
  );
  const childrenByKey = new Map();
  const roots = [];

  normalizedRows.forEach((row) => {
    const parentById =
      row.ACCOUNTPARENTID !== null && row.ACCOUNTPARENTID !== undefined
        ? rowsById.get(String(row.ACCOUNTPARENTID))
        : null;
    const parent = parentById || rowsByCode.get(String(row.PARENTACCOUNTCODE || ''));

    if (!parent || parent.__treeKey === row.__treeKey) {
      roots.push(row);
      return;
    }

    const children = childrenByKey.get(parent.__treeKey) || [];
    children.push(row);
    childrenByKey.set(parent.__treeKey, children);
  });

  const orderedRows = [];
  const visited = new Set();
  const visit = (row, level) => {
    if (visited.has(row.__treeKey)) return;
    visited.add(row.__treeKey);
    const children = (childrenByKey.get(row.__treeKey) || []).sort(compareAccountCodes);
    orderedRows.push({
      ...row,
      TREELEVEL: level,
      HASCHILDREN: children.length > 0,
    });
    children.forEach((child) => visit(child, level + 1));
  };

  roots.sort(compareAccountCodes).forEach((row) => visit(row, 1));
  normalizedRows
    .filter((row) => !visited.has(row.__treeKey))
    .sort(compareAccountCodes)
    .forEach((row) => visit(row, Number(row.ACCOUNTLEVEL || 1)));

  return orderedRows.map(({ __treeKey, __sourceIndex, ...row }) => row);
};

export const getVisibleTrialBalanceRows = (rows = [], collapsedAccountCodes = []) => {
  const collapsedCodes = new Set(collapsedAccountCodes.map(String));
  const collapsedLevels = [];

  return buildTrialBalanceTree(rows).filter((row) => {
    const level = Math.max(1, Number(row.TREELEVEL || row.ACCOUNTLEVEL || 1));
    while (collapsedLevels.length && collapsedLevels[collapsedLevels.length - 1] >= level) {
      collapsedLevels.pop();
    }
    const hidden = collapsedLevels.length > 0;
    if (row.HASCHILDREN && collapsedCodes.has(String(row.ACCOUNTINGACCOUNTCODE))) {
      collapsedLevels.push(level);
    }
    return !hidden;
  });
};

export const buildTrialBalanceExportRows = (rows = []) =>
  buildTrialBalanceTree(rows).map((row) => ({
    'Mã tài khoản': row.ACCOUNTINGACCOUNTCODE || '',
    'Tên tài khoản': `${'  '.repeat(Math.max(0, Number(row.TREELEVEL || row.ACCOUNTLEVEL || 1) - 1))}${row.ACCOUNTINGACCOUNTNAME || ''}`,
    'Tài khoản cha': row.PARENTACCOUNTCODE || '',
    Bậc: row.TREELEVEL || row.ACCOUNTLEVEL || '',
    'Dư Nợ đầu kỳ': Number(row.OPENINGDEBIT || 0),
    'Dư Có đầu kỳ': Number(row.OPENINGCREDIT || 0),
    'Phát sinh Nợ': Number(row.DEBITAMOUNT || 0),
    'Phát sinh Có': Number(row.CREDITAMOUNT || 0),
    'Lũy kế Nợ đầu năm': Number(row.YTDDEBIT || 0),
    'Lũy kế Có đầu năm': Number(row.YTDCREDIT || 0),
    'Dư Nợ cuối kỳ': Number(row.CLOSINGDEBIT || 0),
    'Dư Có cuối kỳ': Number(row.CLOSINGCREDIT || 0),
  }));
